import { inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly currentBaseUrl!: string;
  private lastUrl: string | null = null; // Для хранения последнего URL

  _platformId = inject(PLATFORM_ID);

  constructor(
    @Inject(DOCUMENT) private _doc: any,
    private meta: Meta,
    private title: Title,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.currentBaseUrl = 'https://changeme.rouvas/';
  }

  public autoUpdateTags() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        const currentUrl = this.router.url;

        // Проверка, изменился ли URL
        if (this.lastUrl !== currentUrl) {
          this.lastUrl = currentUrl;
          this.changeUrl(currentUrl);

          if (!data['denyPrepareSeo']) {
            const cleanUrl = currentUrl.split('?')[0].replace(/^\/+/, '');
            // const staticRouteMeta = routesMeta.find((route) => route.path === cleanUrl);
            const staticRouteMeta: any = undefined;

            if (staticRouteMeta) {
              this.changeTitle(staticRouteMeta.title);
              this.changeDescription(staticRouteMeta.description);
            } else {
              if (data['title']) {
                this.changeTitle(data['title']);
              }
              if (data['description']) {
                this.changeDescription(data['description']);
              }
            }
          }

          this.createLinkForCanonicalURL();
        }
      });
  }

  private changeUrl(url: string) {
    url = this.currentBaseUrl + url;
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ name: 'url', content: url });
  }

  public changeTitle(title: string, brand = true) {
    const newTitle = title ? `${title} ${brand ? ' — Rentag' : ''}` : 'Rentag';
    if (this.title.getTitle() !== newTitle) {
      this.title.setTitle(newTitle);
      this.meta.updateTag({ name: 'title', content: newTitle });
      this.meta.updateTag({ property: 'og:title', content: newTitle });
    }
  }

  public changeDescription(description: string) {
    if (description && this.meta.getTag('name=description')?.content !== description) {
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:description', content: description });
    }
  }

  private createLinkForCanonicalURL() {
    if (isPlatformBrowser(this._platformId)) {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.remove();
      }
    }
    const link: HTMLLinkElement = this._doc.createElement('link');
    link.setAttribute('rel', 'canonical');
    this._doc.head.appendChild(link);
    environment.production
      ? link.setAttribute('href', (this.currentBaseUrl + this.router.url).split('?')[0])
      : link.setAttribute('href', this._doc.URL.split('?')[0]);
  }

  public changeImage(url: string) {
    this.meta.updateTag({ property: 'og:image', content: url });
    this.meta.updateTag({ name: 'og:image:secure_url', content: url });
  }

  public getTitle(): string {
    return this.title.getTitle();
  }

  public insertOrgSchemaScript(schemaItem: Array<Record<string, unknown>>): void {
    const schema = {
      ['@context']: 'https://schema.org',
      ['@graph']: [...schemaItem],
    };
    const jsonSchema = `${JSON.stringify(schema, null, 2)}`;

    const node = this._doc.createElement('script');
    node.type = 'application/ld+json';
    node.async = true;
    node.innerHTML = jsonSchema;

    this._doc.getElementsByTagName('head')[0].appendChild(node);
  }

  setKeywords(content: string): void {
    this.meta.addTag({ name: 'keywords', content: content });
  }
}
